import { notFound } from '@tanstack/react-router'

import { HttpStatus, isHttpStatusError } from '@droidmap/shared-utils'

/**
 * Did this failed service call mean the resource genuinely isn't there?
 *
 * 404 is the obvious arm. 403 is here on purpose: the user is being told the
 * org/project/asset-set isn't theirs to see, and `NotFound`'s own copy already
 * says "doesn't exist **or you don't have permission to access it**" — so
 * routing a 403 to the not-found screen is both accurate and the answer that
 * doesn't leak whether the id exists.
 *
 * Note what is *not* here: 5xx, 429, 401, and anything with no status at all.
 * Those mean the call failed, not that the thing is missing, and a route loader
 * that reports them as "not found" makes a false claim about the user's data
 * with no way back (droidmap#2658).
 *
 * The org-service frozen-org gate also answers 403, but it allow-lists
 * `GET /organisations/:orgId` precisely so the frozen CTA can still read the
 * org — a frozen org therefore never reaches this predicate.
 */
export const isResourceMissingError = (error: unknown): boolean =>
  isHttpStatusError(error, HttpStatus.NOT_FOUND_404, HttpStatus.FORBIDDEN_403)

/**
 * Turn a failed service call inside a route loader into the right router
 * signal: `notFound()` when the resource is genuinely missing, otherwise the
 * original error, which the route's `errorComponent` renders with a retry.
 *
 * Always throws. Written as a `function` declaration rather than an arrow
 * const on purpose: only that form lets TypeScript's control-flow analysis see
 * the `never`, and without it every loader that ends a `catch` with this call
 * gets `TLoaderData | undefined` and every consumer of `useLoaderData()`
 * breaks.
 *
 * @example
 * try {
 *   const org = await organisationClient.getOrganisation({ orgId }, metadata)
 *   return { crumb: org.name, org }
 * } catch (error) {
 *   throwLoaderError(error, 'organisation')
 * }
 */
export function throwLoaderError(error: unknown, resource: string): never {
  console.error(`Failed to load ${resource}`, error)
  if (isResourceMissingError(error)) {
    throw notFound()
  }
  throw error
}

/**
 * Why the router is re-running a loader. Mirrors TanStack Router's
 * `LoaderFnContext['cause']`; named here so {@link classifyLoadFailure} can be
 * unit tested without standing up a router.
 */
export type LoaderCause = 'preload' | 'enter' | 'stay'

/**
 * What a route should do about a loader failure.
 *
 * - `not-found` — throw `notFound()`; the resource is gone or not ours.
 * - `stale` — keep showing the value we already had; a revalidation blipped.
 * - `error` — surface the failure; there is nothing good to fall back to.
 */
export type LoadFailureOutcome = 'not-found' | 'stale' | 'error'

/**
 * The full failure matrix for a loader that can fall back to its last good
 * value (droidmap#2658).
 *
 * `stale` is reserved for `cause === 'stay'`: the user is already looking at
 * this match, we have a value that was good moments ago, and a revalidation
 * that blipped is best made invisible. A fresh `enter` or a `preload` has
 * nothing on screen to preserve, so it must surface the real failure rather
 * than silently seat the user on data from a previous visit.
 *
 * Exported and pure for the same reason `orgLayoutView` is: the loader it
 * drives can't be exercised in a unit test, so without this the whole matrix
 * — including the arm that stops a 5xx during the Pro-upgrade settlement poll
 * from replacing the "upgrade in progress" screen with "Organization not
 * found" — would be reachable only by reading it.
 */
export const classifyLoadFailure = (
  error: unknown,
  options: { cause: LoaderCause; hasLastGood: boolean },
): LoadFailureOutcome => {
  if (isResourceMissingError(error)) return 'not-found'
  if (options.cause === 'stay' && options.hasLastGood) return 'stale'
  return 'error'
}

/**
 * Must the caller drop its last-good value after this outcome?
 *
 * The `'error'` arm is the load-bearing one, and it is not obvious. A route's
 * retry control calls `router.invalidate()`, which does **not** remove the
 * errored match from `state.matches` — it only flips its status to `pending`.
 * The router then derives `cause` from `previousMatchesByRouteId`, built from
 * exactly that array, so **a retry after a failed `enter` runs as `'stay'`**.
 * Leave the fallback in place and that retry takes the `'stale'` arm and
 * returns a snapshot from a previous visit as though the call had succeeded —
 * silently, and precisely in the case {@link classifyLoadFailure}'s contract
 * says must surface the real failure.
 *
 * A failed `'preload'` is the exception: it never reaches the user (preloads
 * build `cachedMatches`, so nothing on screen changes), while the org it names
 * may be the one the user is sitting on mid-poll. Dropping the fallback there
 * would re-open the bug this all exists to fix — `defaultPreload: 'intent'`
 * means merely hovering the Projects tab can trigger it.
 *
 * Callers must still check the value belongs to the key that just failed, or a
 * failed load of org B will wipe org A's fallback.
 */
export const shouldForgetLastGood = (
  outcome: LoadFailureOutcome,
  cause: LoaderCause,
): boolean => {
  if (outcome === 'not-found') return true
  if (outcome === 'error') return cause !== 'preload'
  return false
}
